import { useEffect, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { Modal, Stack, Stepper } from '@mantine/core'
import { FacilityStep } from './FacilityStep'
import { DetailsStep } from './DetailsStep'
import { ReviewStep } from './ReviewStep'
import { createInitialAuthorizationFormState } from './authorizationFormData'
import type { AuthorizationFormState, FacilityDetails } from './authorizationFormData'
import { useAuthorizationStatus } from './useAuthorizationStatus'
import { useAuthorizationPdf } from './useAuthorizationPdf'

type Props = {
  isOpen: boolean
  onClose: () => void
  patient: Patient
}

type Step = 'facility' | 'details' | 'review'
const STEPS: Step[] = ['facility', 'details', 'review']

export function AuthorizationReleaseModal({ isOpen, onClose, patient }: Props) {
  const [step, setStep] = useState<Step>('facility')
  const [values, setValues] = useState<AuthorizationFormState>(
    createInitialAuthorizationFormState(),
  )
  const status = useAuthorizationStatus(patient.id ?? '')
  const pdf = useAuthorizationPdf(patient, values, status.signed, status.signedDate)

  // Reset to a clean wizard each time the modal opens (mirrors PCPModal behavior).
  useEffect(() => {
    if (isOpen) {
      setStep('facility')
      setValues(createInitialAuthorizationFormState())
    }
  }, [isOpen])

  const stepIndex = STEPS.indexOf(step)
  const update = (patch: Partial<AuthorizationFormState>) => setValues((v) => ({ ...v, ...patch }))
  const setFacility = (facility: FacilityDetails) => setValues((v) => ({ ...v, facility }))

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Request Outside Records"
      centered
      size="xl"
      padding="xl"
    >
      <Stack gap="lg" px="md" pb="md">
        <Stepper active={stepIndex} size="sm">
          <Stepper.Step label="Facility" description="Where to send" />
          <Stepper.Step label="Details" description="Records & dates" />
          <Stepper.Step label="Review" description="Preview & send" />
        </Stepper>

        {step === 'facility' && (
          <FacilityStep
            patient={patient}
            values={values}
            setFacility={setFacility}
            onCancel={onClose}
            onNext={() => setStep('details')}
          />
        )}
        {step === 'details' && (
          <DetailsStep
            values={values}
            update={update}
            onBack={() => setStep('facility')}
            onNext={() => setStep('review')}
          />
        )}
        {step === 'review' && (
          <ReviewStep
            values={values}
            status={status.status}
            statusLoading={status.loading}
            signed={status.signed}
            onBack={() => setStep('details')}
            onPreview={pdf.handlePreviewPdf}
            previewing={pdf.previewing}
            onSendFax={async () => {
              const ok = await pdf.handleSendFax()
              if (ok) onClose()
            }}
            sending={pdf.sending}
          />
        )}
      </Stack>
    </Modal>
  )
}
